import React, { useState, useEffect, useRef } from 'react';
import { Table, Card, Input, Select, DatePicker, Button, Tag, Space, Modal, message, Tooltip } from 'antd';
import { SearchOutlined, EyeOutlined, StopOutlined, ReloadOutlined, FilterOutlined } from '@ant-design/icons';
import { $api } from 'services/service';
import { ITicket, ITicketType, TicketStatus, getTicketStatusText, getTicketStatusColor, formatCurrency } from '../ticketModels';
import moment from 'moment';
import 'moment/locale/vi';
import locale from 'antd/es/date-picker/locale/vi_VN';
import TicketDetailModal from '../popup/TicketDetailModal';
import TicketUseConfirmModal from '../popup/TicketUseConfirmModal';
import type { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const TicketList: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [tickets, setTickets] = useState<ITicket[]>([]);
    const [ticketTypes, setTicketTypes] = useState<ITicketType[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchText, setSearchText] = useState('');
    const [selectedStatus, setSelectedStatus] = useState<number | undefined>(undefined);
    const [selectedTicketType, setSelectedTicketType] = useState<string | undefined>(undefined);
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [cancelModalVisible, setCancelModalVisible] = useState(false);
    const [confirmUseModalVisible, setConfirmUseModalVisible] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState<ITicket | null>(null);
    const [cancelNote, setCancelNote] = useState('');
    const [cancelLoading, setCancelLoading] = useState(false);
    const [useTicketLoading, setUseTicketLoading] = useState(false);

    useEffect(() => {
        loadTicketTypes();
        loadTickets();
    }, [pageIndex, pageSize]);

    const loadTicketTypes = async () => {
        try {
            const res: any = await $api.ticketType.getAll();
            if (res.result) {
                setTicketTypes(res.result);
            }
        } catch (error) {
            console.error('Error loading ticket types:', error);
        }
    };

    const loadTickets = async () => {
        setLoading(true);
        try {
            const params: any = {
                pageIndex,
                pageSize,
                freeTextSearch: searchText || undefined,
                status: selectedStatus,
                ticketTypeId: selectedTicketType,
                fromDate: dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
                toDate: dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
                includeTransaction: true,
            };

            const res: any = await $api.ticket.getPaging(params);
            if (res.result) {
                setTickets(res.result.items || []);
                setTotalRecords(res.result.totalRecord || 0);
            }
        } catch (error) {
            message.error('Không thể tải danh sách phiếu');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setPageIndex(1);
        loadTickets();
    };

    const handleReset = () => {
        setSearchText('');
        setSelectedStatus(undefined);
        setSelectedTicketType(undefined);
        setDateRange(null);
        setPageIndex(1);
        setTimeout(() => loadTickets(), 100);
    };

    const handleViewDetail = async (ticket: ITicket) => {
        setSelectedTicket(ticket);
        setDetailModalVisible(true);
    };

    const handleCancelTicket = (ticket: ITicket) => {
        setSelectedTicket(ticket);
        setCancelNote('');
        setCancelModalVisible(true);
    };

    const confirmCancelTicket = async () => {
        if (!selectedTicket || !cancelNote.trim()) {
            message.warning('Vui lòng nhập lý do hủy phiếu');
            return;
        }

        setCancelLoading(true);
        try {
            const res: any = await $api.ticket.cancelTicket({
                ticketId: selectedTicket.id,
                note: cancelNote
            });
            if (res.result !== undefined) {
                message.success('Hủy phiếu thành công');
                setCancelModalVisible(false);
                setCancelNote('');
                setSelectedTicket(null);
                loadTickets();
            }
        } catch (error: any) {
            message.error(error?.response?.data?.message || 'Có lỗi xảy ra khi hủy phiếu');
        } finally {
            setCancelLoading(false);
        }
    };

    const handleUseTicket = (ticket: ITicket) => {
        setDetailModalVisible(false);
        setSelectedTicket(ticket);
        setConfirmUseModalVisible(true);
    };

    const confirmUseTicket = async () => {
        if (!selectedTicket) return;

        setUseTicketLoading(true);
        try {
            const res: any = await $api.ticket.useTicket({ ticketId: selectedTicket.id });
            if (res.result) {
                message.success('Sử dụng phiếu thành công!');
                setConfirmUseModalVisible(false);
                setSelectedTicket(null);
                loadTickets();
            }
        } catch (e: any) {
            let errMsg;
            let type = typeof e?.response?.data?.errors?.exceptionMessage;
            if (type === 'string') {
                errMsg = e?.response?.data?.errors?.exceptionMessage;
            } else if (e?.response?.data?.errors?.exceptionMessage?.title) {
                errMsg = e?.response?.data?.errors?.exceptionMessage?.title;
            } else {
                errMsg = 'Có lỗi xảy ra khi sử dụng phiếu';
            }
            message.error(errMsg);
        } finally {
            setUseTicketLoading(false);
        }
    };

    const handleTicketUpdate = (updatedTicket: ITicket) => {
        setSelectedTicket(updatedTicket);
        loadTickets();
    };

    const columns = [
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Mã phiếu</span>,
            dataIndex: 'code',
            key: 'code',
            width: 110,
            align: 'left' as const,
            render: (code: string) => (
                <span className="font-mono font-bold text-sm sm:text-base text-blue-600">{code}</span>
            ),
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Sinh viên</span>,
            key: 'student',
            width: 180,
            align: 'left' as const,
            render: (record: ITicket) => (
                <div>
                    <div className="font-semibold text-sm sm:text-base break-words">{record.studentName}</div>
                    <div className="text-gray-500 text-xs sm:text-sm font-medium">{record.studentUserInternalId}</div>
                </div>
            ),
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Loại phiếu</span>,
            dataIndex: 'ticketTypeName',
            key: 'ticketTypeName',
            width: 150,
            align: 'left' as const,
            render: (name: string) => <span className="font-semibold text-sm sm:text-base break-words">{name}</span>,
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Giá</span>,
            dataIndex: 'price',
            key: 'price',
            width: 100,
            align: 'right' as const,
            render: (price: number) => (
                <span className="font-bold text-green-600 text-sm sm:text-lg">{formatCurrency(price)}</span>
            ),
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Ngày tạo</span>,
            dataIndex: 'creationDate',
            key: 'creationDate',
            width: 140,
            align: 'center' as const,
            render: (date: string) => {
                return (
                    <span className='text-green-600 font-semibold text-xs sm:text-base'>
                        {moment(date).format('HH:mm DD/MM/YY')}
                    </span>
                );
            },
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Trạng thái</span>,
            dataIndex: 'status',
            key: 'status',
            width: 130,
            align: 'center' as const,
            render: (status: TicketStatus) => (
                <Tag color={getTicketStatusColor(status)} className="text-xs sm:text-base font-semibold py-0.5 sm:py-1">
                    {getTicketStatusText(status)}
                </Tag>
            ),
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Người bán</span>,
            dataIndex: 'createdByName',
            key: 'createdByName',
            width: 140,
            align: 'left' as const,
            render: (name: string) => <span className="font-semibold text-sm sm:text-base break-words">{name}</span>,
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Thao tác</span>,
            key: 'action',
            width: 140,
            align: 'center' as const,
            fixed: 'right' as const,
            render: (record: ITicket) => (
                <Space size="small">
                    <Tooltip title="Chi tiết">
                        <Button
                            type="primary"
                            className="focus:outline-none focus:shadow-none active:shadow-none text-xs sm:text-sm"
                            ghost
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => handleViewDetail(record)}
                        >
                            <span className="hidden sm:inline">Chi tiết</span>
                        </Button>
                    </Tooltip>
                    {record.status === TicketStatus.Active && (
                        <Tooltip title="Hủy phiếu">
                            <Button
                                className="focus:outline-none focus:shadow-none active:shadow-none"
                                danger
                                size="small"
                                icon={<StopOutlined />}
                                onClick={() => handleCancelTicket(record)}
                            />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="space-y-3 sm:space-y-4">
            {/* Filter Section */}
            <Card className="shadow-md">
                <div className="space-y-3 sm:space-y-4">
                    <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
                        <FilterOutlined />
                        Bộ lọc
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                        <Input
                            placeholder="Mã phiếu, sinh viên..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onPressEnter={handleSearch}
                            prefix={<SearchOutlined />}
                            size="middle"
                        />

                        <Select
                            placeholder="Trạng thái"
                            allowClear
                            value={selectedStatus}
                            onChange={setSelectedStatus}
                        >
                            <Option value={TicketStatus.Active}>Đang hoạt động</Option>
                            <Option value={TicketStatus.Used}>Đã sử dụng</Option>
                            <Option value={TicketStatus.Expired}>Hết hạn</Option>
                            <Option value={TicketStatus.Canceled}>Đã hủy</Option>
                        </Select>

                        <Select
                            placeholder="Loại phiếu"
                            allowClear
                            value={selectedTicketType}
                            onChange={setSelectedTicketType}
                        >
                            {ticketTypes.map(type => (
                                <Option key={type.id} value={type.id}>
                                    {type.name}
                                </Option>
                            ))}
                        </Select>

                        <RangePicker
                            placeholder={['Từ ngày', 'Đến ngày']}
                            value={dateRange}
                            onChange={(dates) => setDateRange(dates)}
                            format="DD/MM/YYYY"
                            locale={locale}
                            size="middle"
                            className="w-full"
                        />
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="primary"
                            className="focus:outline-none focus:shadow-none active:shadow-none text-xs sm:text-sm"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            size="middle"
                        >
                            Tìm
                        </Button>
                        <Button
                            className="focus:outline-none focus:shadow-none active:shadow-none text-xs sm:text-sm"
                            icon={<ReloadOutlined />}
                            onClick={handleReset}
                            size="middle"
                        >
                            <span className="hidden xs:inline">Đặt lại</span>
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Table Section */}
            <Card className="shadow-md overflow-hidden">
                <div className="mb-2 sm:mb-4">
                    <h3 className="text-base sm:text-xl font-bold text-gray-800">Danh sách phiếu</h3>
                </div>
                <Table
                    columns={columns}
                    dataSource={tickets}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 1100 }}
                    size="small"
                    bordered
                    rowClassName={(record, index) =>
                        index % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-gray-50 hover:bg-blue-50'
                    }
                    pagination={{
                        current: pageIndex,
                        pageSize: pageSize,
                        total: totalRecords,
                        showSizeChanger: true,
                        size: 'small',
                        showTotal: (total) => <span className="font-semibold text-xs sm:text-base">Tổng {total}</span>,
                        onChange: (page, size) => {
                            setPageIndex(page);
                            setPageSize(size || 10);
                        },
                    }}
                />
            </Card>

            {/* Detail Modal - Using Shared Component */}
            <TicketDetailModal
                visible={detailModalVisible}
                ticket={selectedTicket}
                onClose={() => setDetailModalVisible(false)}
                onTicketUpdate={handleTicketUpdate}
                showUseButton={true}
                onUseTicket={handleUseTicket}
            />

            {/* Use Confirm Modal - Using Shared Component */}
            <TicketUseConfirmModal
                visible={confirmUseModalVisible}
                ticket={selectedTicket}
                loading={useTicketLoading}
                onConfirm={confirmUseTicket}
                onCancel={() => setConfirmUseModalVisible(false)}
            />

            {/* Cancel Modal */}
            <Modal
                title="Hủy phiếu"
                open={cancelModalVisible}
                onCancel={() => {
                    setCancelModalVisible(false);
                    setCancelNote('');
                }}
                footer={[
                    <Button key="close" onClick={() => setCancelModalVisible(false)}>
                        Đóng
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        danger
                        loading={cancelLoading}
                        onClick={confirmCancelTicket}
                    >
                        Xác nhận hủy
                    </Button>
                ]}
                width={600}
            >
                {selectedTicket && (
                    <div className="space-y-4">
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                            <p className="font-semibold mb-2">Thông tin phiếu:</p>
                            <p>Mã phiếu: <span className="font-mono font-bold">{selectedTicket.code}</span></p>
                            <p>Sinh viên: <span className="font-semibold">{selectedTicket.studentName}</span></p>
                            <p>Loại phiếu: {selectedTicket.ticketTypeName}</p>
                        </div>

                        <div>
                            <label className="block mb-2 font-semibold text-red-600">
                                Lý do hủy phiếu <span className="text-red-500">*</span>
                            </label>
                            <TextArea
                                rows={4}
                                value={cancelNote}
                                onChange={(e) => setCancelNote(e.target.value)}
                                placeholder="Nhập lý do hủy phiếu (bắt buộc)..."
                            />
                        </div>

                        <div className="bg-red-50 border border-red-200 rounded p-3">
                            <p className="text-red-600 font-semibold">⚠️ Cảnh báo:</p>
                            <p className="text-sm text-gray-700">Sau khi hủy, phiếu sẽ không thể sử dụng lại.</p>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default TicketList;
